import {
  type ActionResult,
  type Billing,
  PLAN_CATALOG,
  PLAN_PRICES,
  PLANS,
  Plan,
} from "@brandwall/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ApiError, api, post } from "../api";
import { useWorkspace } from "../components/Shell";
import { useToast } from "../components/Toast";

// Names, prices and what each plan includes come from plans.json (D-083), like the website's.
const PLAN_LIST = Plan.options.map((id) => ({
  id,
  name: PLANS[id].name,
  features: PLANS[id].bullets,
}));
const RANK: Record<Plan, number> = { free: 0, starter: 1, pro: 2, agency: 3 };

/** Wall views this month against the plan's monthly limit (D-083). */
function UsageMeter({
  views,
  limit,
  resetsOn,
}: {
  views: number;
  limit: number;
  resetsOn: string;
}) {
  const share = Math.min(1, views / limit);
  const resets = new Date(`${resetsOn}T12:00:00Z`).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
  });
  const full = views >= limit;
  return (
    <div className={full ? "error" : "card stack"} role={full ? "alert" : undefined}>
      <div className="row">
        <b className="spacer">Wall views this month</b>
        <span className="small">
          {views.toLocaleString("en-GB")} of {limit.toLocaleString("en-GB")}
        </span>
      </div>
      <div className="meter" aria-hidden="true">
        <i style={{ width: `${share * 100}%` }} />
      </div>
      <span className="small muted">
        {full
          ? `Your walls don't show on your website until ${resets}. Upgrade to show them again now.`
          : `The count starts again on ${resets}. When it reaches ${limit.toLocaleString("en-GB")}, your walls stop showing until then.`}
      </span>
    </div>
  );
}

export function PlanTab() {
  const { ws } = useParams();
  const workspace = useWorkspace();
  const isOwner = workspace.role === "owner";
  const qc = useQueryClient();
  const toast = useToast();
  const [yearly, setYearly] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const billing = useQuery({
    queryKey: ["billing", ws],
    queryFn: () => api<Billing>(`/v1/w/${ws}/billing`),
  });
  const b = billing.data;
  if (!b) return <p className="muted">Loading…</p>;
  const paid = b.status && ["active", "trialing", "past_due"].includes(b.status);

  const trial = async (plan: Plan) => {
    try {
      const r = await post<Billing & { result: ActionResult }>(`/v1/w/${ws}/billing/trial`, {
        plan,
      });
      qc.setQueryData(["billing", ws], r);
      await qc.invalidateQueries({ queryKey: ["me"] });
      toast(r.result.summary);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  const checkout = async (plan: Plan) => {
    try {
      const r = await post<{ url: string }>(`/v1/w/${ws}/billing/checkout`, {
        plan,
        interval: yearly ? "year" : "month",
      });
      window.location.href = r.url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };
  const portal = async () => {
    try {
      window.location.href = (await post<{ url: string }>(`/v1/w/${ws}/billing/portal`, {})).url;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "Something went wrong.");
    }
  };

  return (
    <div className="stack">
      {error && <div className="error">{error}</div>}
      {new URLSearchParams(window.location.search).get("paid") && (
        <div className="notice">
          Thank you! Your new plan starts as soon as Stripe confirms the payment.
        </div>
      )}
      {b.granted && (
        <div className="notice">
          Brandwall gave you {b.granted.plan[0]!.toUpperCase() + b.granted.plan.slice(1)}
          {b.granted.until
            ? ` until ${new Date(b.granted.until).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}`
            : ""}
          . You aren't charged for it.
        </div>
      )}
      {b.trial && (
        <div className="notice">
          You're trying {b.trial.plan[0]!.toUpperCase() + b.trial.plan.slice(1)} for free until{" "}
          {new Date(b.trial.endsAt).toLocaleDateString("en-GB", { day: "numeric", month: "long" })}.
          No card needed.
        </div>
      )}
      {b.usage.viewLimit !== null && (
        <UsageMeter
          views={b.usage.viewsThisMonth}
          limit={b.usage.viewLimit}
          resetsOn={b.usage.resetsOn}
        />
      )}
      {b.usage.pausedWalls.length > 0 && (
        <div className="error" role="alert">
          Your plan covers {b.wallsUsed - b.usage.pausedWalls.length} live{" "}
          {b.wallsUsed - b.usage.pausedWalls.length === 1 ? "wall" : "walls"}, so{" "}
          {b.usage.pausedWalls.join(", ")} {b.usage.pausedWalls.length === 1 ? "isn't" : "aren't"}{" "}
          showing on your website. Upgrade, or unpublish a wall you don't need.
        </div>
      )}
      {b.founding.eligible && b.cardPayments && (
        <div className="card row wrap" style={{ borderColor: "var(--berry)" }}>
          <p className="spacer">
            <b>Founding customer offer:</b> the first 10 paying brands get 50% off their first year.{" "}
            {b.founding.left} places left.
          </p>
        </div>
      )}
      <div className="row">
        <span className="spacer muted small">
          {PLAN_CATALOG.notes.prices} {PLAN_CATALOG.notes.everyPlan}
        </span>
        <div className="seg" role="group" aria-label="Billing period">
          <button type="button" aria-pressed={!yearly} onClick={() => setYearly(false)}>
            Monthly
          </button>
          <button type="button" aria-pressed={yearly} onClick={() => setYearly(true)}>
            Yearly
          </button>
        </div>
      </div>
      <div className="plans">
        {PLAN_LIST.map((p) => {
          const current = b.plan === p.id;
          const price = yearly ? Math.round((PLAN_PRICES[p.id] * 10) / 12) : PLAN_PRICES[p.id];
          return (
            <section key={p.id} className={`card stack plan ${current ? "current" : ""}`}>
              <div className="row">
                <h2 className="spacer">{p.name}</h2>
                {current && <span className="pill ok">Current plan</span>}
              </div>
              <div>
                <span className="stat">€{price}</span> <span className="muted">/ month</span>
              </div>
              <ul className="checks small">
                {p.features.map((f) => (
                  <li key={f}>{f}</li>
                ))}
              </ul>
              {isOwner && !current && p.id !== "free" && (
                <div className="stack" style={{ gap: 6, marginTop: "auto" }}>
                  {b.cardPayments && !paid && (
                    <button type="button" className="btn" onClick={() => checkout(p.id)}>
                      {RANK[p.id] > RANK[b.plan] ? "Upgrade" : "Switch"}
                    </button>
                  )}
                  {b.trialAvailable && !paid && (
                    <button
                      type="button"
                      className={`btn ${b.cardPayments ? "secondary" : ""}`}
                      onClick={() => trial(p.id)}
                    >
                      Try free for 14 days
                    </button>
                  )}
                  {paid && (
                    <button type="button" className="btn secondary" onClick={portal}>
                      {RANK[p.id] > RANK[b.paidPlan] ? "Upgrade" : "Downgrade"}
                    </button>
                  )}
                </div>
              )}
              {isOwner && current && paid && (
                <button
                  type="button"
                  className="btn secondary"
                  onClick={portal}
                  style={{ marginTop: "auto" }}
                >
                  Manage
                </button>
              )}
            </section>
          );
        })}
      </div>
      <section className="card row wrap">
        <span className="spacer">
          <b>Walls in use</b> {b.wallsUsed} wall{b.wallsUsed === 1 ? "" : "s"}
          {b.wallNames.length ? ` · ${b.wallNames.join(", ")}` : ""}
          {b.plan === "pro" || b.plan === "agency"
            ? ` · unlimited on ${b.plan[0]!.toUpperCase() + b.plan.slice(1)}`
            : ""}
        </span>
        <Link className="btn secondary sm" to={`/w/${ws}/walls`}>
          + Add a wall
        </Link>
      </section>
      <section className="card stack">
        <h2>Billing</h2>
        {paid ? (
          <>
            <p className="muted">
              {b.paidPlan[0]!.toUpperCase() + b.paidPlan.slice(1)}, paid{" "}
              {b.interval === "year" ? "yearly" : "monthly"} by card
              {b.currentPeriodEnd
                ? `, renews ${new Date(b.currentPeriodEnd).toLocaleDateString("en-GB")}`
                : ""}
              .{b.founding.applied ? " Founding customer: 50% off the first year." : ""}
              {b.status === "past_due" ? " The last payment failed. Please update your card." : ""}
            </p>
            {isOwner && (
              <button
                type="button"
                className="btn secondary"
                onClick={portal}
                style={{ justifySelf: "start" }}
              >
                Card and invoices
              </button>
            )}
          </>
        ) : (
          <p className="muted">
            {b.cardPayments
              ? "Pay by card. Invoices include EU VAT, and you can cancel at any time."
              : "Card payments aren't open yet. Until then, you can try any paid plan free for 14 days."}
          </p>
        )}
        {b.agencyBrands > 0 && (
          <p className="small muted">{b.agencyBrands} brands on this Agency plan (up to 10).</p>
        )}
      </section>
    </div>
  );
}
